import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import GraphicLayout from './layout/graphic';
import './App.less';
import '@/declare/styles/index.less';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN'
import { useEffect, useState } from 'react';
import { userCheckLogin } from './declare/api/project/user';
import { User } from './declare/interface/project/user';
import localStore, { USER_NAME, USER_UUID } from './store/local-store';
import React from 'react';
import { createContext } from 'react';
import { EngineStatus, useEngineService } from './store/engine-store';
import { NavigationStatus, useNavigationService } from './store/navigation-context';
import Login from './layout/login/login';
import GlobalLayout from './layout/global';
import { CmdStatus, useCmdService } from './store/cmd-store';
import 'bimui/dist/bimui.css';
import 'bim-structure-web/dist/bim-structure.css';
import 'bim-monix-web/dist/bim-monix.css';
import { plugin as structureWebPlugin } from 'bim-structure-web'
import { plugin as monixWebPlugin } from 'bim-monix-web'
import { RegisterStatus, useRegisterService } from './store/register-store';
import GeoMenu from './layout/graphic/menu/geo-menu';
import ProjectMenu from './layout/graphic/menu/project-menu';
import MaterialPanelComponent from './panel/material';
import { useFeedbackUploadService, UserFeedbackUploadFileState } from './components/user-feedback/upload-panel';
import UserFeedBackPanel from './components/user-feedback';
import GroupPanel from './panel/group';
import UserDrawer from './panel/user/user-drawer';
import { PositionStatus, usePositionService } from './store/position-store';
import SectionPanelComponent from './panel/new-section';
import ParamPanel from './panel/param';
import { SIDEBAR_TYPE, TOOL_TYPE } from './declare/enums/menu-type';
import SystemLibLayout from './layout/system-lib';
import SystemLibMenu from './layout/system-lib/system-lib-menu';
import UcsPanel from './panel/ucs';
import BlockPanel from './panel/block';
import UserHelpModal from './components/user-help';

export const EngineContext = createContext<EngineStatus>(null);
export const NavigationContext = React.createContext<NavigationStatus>(null);
export const RegisterContext = React.createContext<RegisterStatus>(null);
export const UserFeedbackUploadFileContext = React.createContext<UserFeedbackUploadFileState>(null);
export const CmdContext = createContext<CmdStatus>(null);
export const PositionContext = createContext<PositionStatus>(null);

function App() {
    const navigate = useNavigate();
    const location = useLocation(); // 获取当前路由的路径
    const navigationContext = useNavigationService();
    const engineContext = useEngineService();
    const cmdContext = useCmdService();
    const registerContext = useRegisterService();
    const positionContext = usePositionService();
    const [visible, setVisible] = useState(false);  // 意见反馈弹窗
    const [userHelpVisible, setUserHelpVisible] = useState(false);  // 用户帮助弹窗

    let timer$;

    /**
     * 定期校验登录
     */
    useEffect(() => {
        checkLogin();
        timer$ = setInterval(() => {
            checkLogin();
        }, 60000)
        return (() => {
            clearInterval(timer$);
        })
    }, [])


    /**
     * 校验登录
     * @returns 
     */
    const checkLogin = () => {
        let token = localStore.token || '';
        if (token.length <= 0) {
            if (!location.pathname.includes('login')) {
                localStore.clear();
                navigate('/login')
            }
            return;
        }
        userCheckLogin()
            .then(res => {
                let userDetail: User = res.data;
                localStore.set(USER_UUID, userDetail.uuid);
                localStore.set(USER_NAME, userDetail.name);
                navigationContext.setIsDemoUser(userDetail.type === 3);
            }).catch(err => {
                localStore.clear();
                navigate('/login')
            })
    }

    /**
     * 注册
     */
    useEffect(() => {
        // 内置专业
        const core = {
            uuid: "project",
            name: "项目",
            menu: { title: '项目', pluginUuid: "project", menuItem: ProjectMenu },
            systemLibMenu: {
                title: '系统库',
                pluginUuid: "project",
                menuItem: SystemLibMenu
            },
            sidebarPanels: {
                [SIDEBAR_TYPE.MATERIAL]: MaterialPanelComponent,
                [SIDEBAR_TYPE.SECTION]: SectionPanelComponent,
                [SIDEBAR_TYPE.MODEL]: GroupPanel,
                [SIDEBAR_TYPE.SYSTEM_PARAM]: ParamPanel,
            },
            toolPanels: {
                [TOOL_TYPE.BLOCK]: BlockPanel,
                [TOOL_TYPE.UCS]: UcsPanel,
            },
        }

        // 注册专业
        registerContext.registMajors([structureWebPlugin, monixWebPlugin].map(item => {
            return {
                uuid: item.uuid,
                name: item.name,
            }
        }))

        // 注册菜单
        registerContext.registMenus([core.menu, structureWebPlugin.menu, monixWebPlugin.menu]);

        // 注册项目专业库菜单
        registerContext.registLibMenus([structureWebPlugin.libMenu, monixWebPlugin.libMenu]);

        // 注册系统库菜单
        registerContext.registSystemLibMenus([core.systemLibMenu, structureWebPlugin.systemLibMenu, monixWebPlugin.systemLibMenu])

        // 注册侧边栏        
        registerContext.registSidebarPanels({
            ...core.sidebarPanels,  // 注册核心系统侧边栏
            ...structureWebPlugin.sidebarPanels,  // 注册结构侧边栏
            ...monixWebPlugin.sidebarPanels,  // 注册监测侧边栏
        });

        // 注册工具面板
        registerContext.registToolPanels({
            ...core.toolPanels,  // 注册核心系统工具面板
            ...structureWebPlugin.toolPanels,  // 注册结构工具面板
            ...monixWebPlugin.toolPanels,  // 注册监测工具面板
        });

        // 注册内容区路由
        registerContext.registRoutes([...structureWebPlugin.routes, ...monixWebPlugin.routes]);

    }, [])

    return (
        <ConfigProvider
            locale={zhCN}
            theme={{
                components: {
                    Radio: {
                        dotSize: 10,
                        radioSize: 14,
                        buttonPaddingInline: 8,
                        wrapperMarginInlineEnd: 4,
                    },
                },
            }}
        >
            <NavigationContext.Provider value={navigationContext}>
                <EngineContext.Provider value={engineContext}>
                    <RegisterContext.Provider value={registerContext}>
                        <CmdContext.Provider value={cmdContext}>
                            <PositionContext.Provider value={positionContext}>
                                <UserFeedbackUploadFileContext.Provider value={useFeedbackUploadService()}>
                                    <div className="tn-base">
                                        <Routes>
                                            <Route path='/login/*' element={<Login />} />
                                            <Route path='/desktop/:projectGroupUuid' element={<GlobalLayout />} />
                                            <Route path='/desktop' element={<GlobalLayout />} />
                                            <Route path='/projects/:projectUuid/*' element={<GraphicLayout feedbackVisible={visible} changeFeedbackVisible={(value) => setVisible(value)} changeUserHelpVisible={(value) => setUserHelpVisible(value)} />} />
                                            <Route path='/system-lib' element={<SystemLibLayout />} />
                                            <Route path='/system-lib/*' element={<SystemLibLayout />} />
                                            <Route path="*" element={<Navigate to="/login" />} />
                                        </Routes>
                                    </div>
                                    <UserFeedBackPanel visible={visible} changeVisible={(value) => setVisible(value)} />
                                    <UserHelpModal visible={userHelpVisible} changeVisible={(value) => setUserHelpVisible(value)} />
                                </UserFeedbackUploadFileContext.Provider >
                            </PositionContext.Provider>
                        </CmdContext.Provider>
                    </RegisterContext.Provider>
                </EngineContext.Provider>
            </NavigationContext.Provider>

        </ConfigProvider>
    );
}

export default App;
